<template>
  <div>
    <vs-popup class="order-request-dialog"  title="" :active.sync="requestDialogStatus" button-close-hidden>
      <vs-card>
        <div
          class="w-12 h-12 mb-4 bg-yellow-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-opacity-75"
        >
          <feather-icon icon="InfoIcon" svgClasses="h-7 w-7" class="text-yellow-700" />
        </div>
        <p class="text-2xl font-bold mb-4">Reorder Request</p>
        <p class="mb-10 text-muted" style="font-size: 14px;">
          Reordering this request will add the same products to your cart. 
          Any items already in your cart will also be included during checkout. Please review all items carefully before proceeding.
        </p>
        <div class="flex flex-col justify-end w-full">
          <div class="flex gap-4 justify-end">
            <vs-button
              @click="requestDialogStatus=false"
              class="font-semibold px-6 py-4"
              type="flat"
            >Cancel</vs-button>
            <vs-button
              class="px-8 py-4 rounded-lg font-semibold"
              color="primary"
              @click="handleProceedCheckout"
            >Proceed to Checkout</vs-button>
          </div>
        </div>
      </vs-card>
    </vs-popup>
    <vs-card>
      <vs-row vs-align="flex-start" vs-type="flex" vs-justify="left" vs-w="12">
      </vs-row>
      <div id="data-list-list-view" class="data-list-container">
        <div class="flex flex-col lg:hidden">
          <div v-for="order in requests" :key="order._id" class="flex flex-col flex-wrap w-full">
            <!-- product -->
            <div class="flex flex-col justify-center">
              <div class="
                  text-lg
                  lg:text-2xl
                  md:text-2xl
                  lg:font-semibold
                  md:font-semibold
                  cursor-pointer
                ">
                {{ order.createdAt | date_formatter }}
              </div>
              <div class="text-lg">Request #: {{ order.requestNumber }}</div>
              <div class="
                  text-lg
                  lg:text-2xl
                  md:text-2xl
                  font-semibold
                  text-primary
                  lg:mt-2
                  md:mt-2
                ">
                ${{ getTotalPrice(order.items || []) | formatDecimals }}
              </div>
              <div class="text-lg my-2">
                <vs-chip 
                  :color="order.status.toUpperCase() === 'APPROVED' ? 'success' : order.status.toUpperCase() === 'REJECTED' ? 'danger' : ''" 
                  class="whitespace-no-wrap"
                >
                  {{ order.status.toUpperCase() === 'APPROVED' ? 'Orders Placed' : order.status.toUpperCase() === 'REJECTED' ? 'No Orders Placed' : upperFirstStatus(order) }}
                </vs-chip>
              </div>
              <div class="text-lg">
                Items: {{ getQuantityTotal(order.items || []) }}
              </div>
            </div>
            <vs-divider />
          </div>
        </div>

        <vs-table class="hidden lg:block" ref="table" :sst="true" :total="totalDocs" :data="requests" @search="handleSearch"
          @change-page="handleChangePage" @sort="handleSort" :max-items="dataTableParams.limit" search
          :noDataText="noDataText">
          <div slot="header" class="flex flex-wrap-reverse flex-grow justify-left items-top">
            <vs-select placeholder="10" autocomplete v-model="dataTableParams.limit">
              <vs-select-item :key="index" :value="item" :text="item" v-for="(item, index) in limitOptions" />
            </vs-select>
            <div class="ml-5 customClinicStyle">
              <Clinic
                :onChange="() => { }"
                :title="''"
                :isDoctorApproval="true"/>
            </div>
          </div>

          <template slot="thead">
            <vs-th>Clinic Name</vs-th>
            <vs-th sort-key="requestNumber">Request ID</vs-th>
            <vs-th sort-key="status">Request Status</vs-th>
            <vs-th sort-key="requestDate">Request Date</vs-th>
            <vs-th>Total</vs-th>
            <vs-th>Actions</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
              <vs-td>
                <p class="font-bold mb-1">{{ tr.clinicName }}</p>
                <div v-for="(item, index) in tr.items" :key="index">
                  <p class="text-sm">{{ `${item.quantity} x ${item.name}` }}</p>
                </div>
              </vs-td>
              <vs-td>
                {{ tr.requestNumber }}
              </vs-td>
              <vs-td>
                <vs-chip 
                  :color="getBackgroundColor(tr.status.toUpperCase())" 
                  class="whitespace-no-wrap"
                >
                  {{ tr.status.toUpperCase() === 'APPROVED' ? 'Orders Placed' : tr.status.toUpperCase() === 'REJECTED' ? 'No Orders Placed' : upperFirstStatus(tr) }}
                </vs-chip>
              </vs-td>
              <vs-td :data="data[indextr].requestDate">{{
                  data[indextr].requestDate | date_formatter
              }}</vs-td>
              <vs-td :data="data[indextr].total">{{ currency }}
                ${{ getTotalPrice(tr.items || []) | formatDecimals }}
              </vs-td>
              <vs-td :data="data[indextr].total">
                <vs-button type="border" size="small" v-if="hasChildOrderAndFailed(tr)" @click="handleSelectedRequest(tr._id)">
                  Reorder
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>

        <div class="mt-4 flex flex-col md:flex-row items-starts md:items-center justify-start md:justify-between space-y-3 md:space-y-0">
          <span>
            {{
                dataTableParams.page * dataTableParams.limit -
                (dataTableParams.limit - 1)
            }}
            -
            {{
                totalDocs - dataTableParams.page * dataTableParams.limit > 0
                  ? dataTableParams.page * dataTableParams.limit
                  : totalDocs
            }}
            of {{ totalDocs }}
          </span>
          <div v-if="serverResponded">
            <paginate :page-count="totalPage" :click-handler="handleChangePage" class="pagination" :page-range="9"
              :prevText="'<'" :nextText="'>'"></paginate>
          </div>
        </div>
      </div>
    </vs-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Clinic from '../../shop/clinic';
import { upperFirst } from 'lodash';
import { toFixed } from 'accounting-js';
import { REQUEST_STATUS_BG_COLOR, ROUTE_BY_USERTYPE } from './constant';

export default {
  name: 'ClinicOrderRequestList',
  components: {
    Clinic,
  },
  data() {
    return {
      noDataText: 'Loading..',
      totalDocs: 0,
      currency: process.env.VUE_APP_CURRECY,
      dataTableParams: {
        search: '',
        sort: 'requestDate',
        dir: 'desc',
        page: 1,
        limit: 25,
      },
      serverResponded: false,
      limitOptions: [5, 10, 25, 50, 100],
      awaitingSearch: null,
      requests: [],
      loading: false,
      requestDialogStatus:false,
      selectedRequest:'',
    };
  },
  methods: {
    ...mapActions('ecommerce', [
      'fetchOrderRequestList',
    ]),
    submitNewRequest(id) {
      let role = this.userTypeAdmin === 'superAdmin' 
        ? 'superAdmin' : localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}Role`) || '';

      if (role.toLowerCase() === 'staff') {    
        const user = localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`);
        role = user.userType ? user.userType : role;
      }
      this.$router.push({name: ROUTE_BY_USERTYPE[role.toUpperCase().replace(/\s+/g, '_')], params: {requestId: id}});
    },
    hasChildOrderAndFailed(data) {
      return !data.childRequestId && data.status.toUpperCase() === 'FAILED';
    },
    getBackgroundColor(color) {
      return REQUEST_STATUS_BG_COLOR[color];
    },
    upperFirstStatus(order) {
      return upperFirst(order.status);
    },
    getTotalPrice(items) {
      return (items || []).reduce((a, b) => {
        const itemTotalPrice = toFixed(((b.price * b.quantity) / 100), 2) * 100;
        return toFixed((a + itemTotalPrice)/ 100, 2) * 100;
      }, 0);
    },
    getQuantityTotal(data) {
      return data.reduce((a, b) => a + (b['quantity'] || 0), 0);
    },
    getOrderedProductList() {
      this.$vs.loading();
      this.requests = [];
      this.fetchOrderRequestList(this.dataTableParams).then((res) => {
        this.$vs.loading.close();
        this.requests = res.data.data.docs;
        this.totalDocs = res.data.data.pagination.total;
        this.page = res.data.data.pagination.page;
        this.serverResponded = true;
      }).finally(() => {
        this.noDataText = 'No Order Request Available';
        this.$vs.loading.close();
      });
    },
    handleSearch(searching) {
      if (this.awaitingSearch) {
        clearTimeout(this.awaitingSearch);
        this.awaitingSearch = null;
      }
      this.awaitingSearch = setTimeout(() => {
        this.serverResponded = false;
        this.dataTableParams.search = searching;
        this.dataTableParams.page = 1;
        this.$refs.table.currentx = 1;
        this.getOrderedProductList();
      }, 500);
    },
    handleChangePage(page) {
      this.dataTableParams.page = page;
      this.getOrderedProductList();
    },
    handleSort(key, active) {
      this.serverResponded = false;
      this.dataTableParams.sort = key;
      this.dataTableParams.dir = active;
      this.dataTableParams.page = 1;
      this.$refs.table.currentx = 1;
      this.getOrderedProductList();
    },
    handleSelectedRequest(requestId) {
      this.selectedRequest = requestId;
      this.requestDialogStatus = true;
    },
    handleProceedCheckout() {
      this.submitNewRequest(this.selectedRequest);
      this.requestDialogStatus = false;
    },
  },
  computed: {
    totalPage: function () {
      return this.totalDocs / this.dataTableParams.limit >
        parseInt(this.totalDocs / this.dataTableParams.limit)
        ? parseInt(this.totalDocs / this.dataTableParams.limit) + 1
        : parseInt(this.totalDocs / this.dataTableParams.limit);
    },
  },
  watch: {
    loading: function(isLoading) {
      if (isLoading) {
        return this.$vs.loading();
      }
      this.$vs.loading.close();
    },
    'dataTableParams.page': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.dataTableParams.page = newVal;
        this.getOrderedProductList();
      }
    },
    'dataTableParams.limit': function (newlimit, oldLimit) {
      if (newlimit !== oldLimit) {
        this.serverResponded = false;
        this.dataTableParams.page = 1;
        this.dataTableParams.limit = newlimit;
        this.getOrderedProductList();
      }
    },
    'dataTableParams.filter': function (newfilterVal, oldFilterVal) {
      if (newfilterVal !== oldFilterVal) {
        this.serverResponded = false;
        this.dataTableParams.page = 1;
        this.dataTableParams.filter = newfilterVal;
        this.getOrderedProductList();
      }
    },
    '$store.state.clinicId': function () {
      this.getOrderedProductList();
    },
  },
  created() {
    this.getOrderedProductList();
  },
};
</script>

<style lang="scss" scoped>
.customClinicStyle {
  width: 200px;
  z-index: 999;
}

.vs-button-success.vs-button-flat {
  background: rgba(var(--vs-success),.08)!important;
}

.disabled {
  pointer-events: none;
  cursor: default;
  background: #f0f0f0;
  color: #919191;
}

td:first-child{
  white-space: normal;
  min-width: 230px;
}

</style>

<style lang="scss">
 @media screen and (max-width: 720px) {
  .custom-tab {
    .sr-root {
      padding-top: 8px !important;
      overflow: hidden !important;
      min-width: 320px !important;
    }
    .sr-main {
      padding: 2rem 1rem !important;
      min-width: 320px !important;
      height: 80vh !important;
      justify-content: start;
      overflow-y: auto !important;
      overflow-x: hidden !important;
    }
  }
 }

 [dir] .order-request-dialog .vs-popup .vs-popup--content {
    padding: 15px 10px !important;
  }
  
  .order-request-dialog .con-vs-card {
    border: none !important;
    margin-bottom: 0px !important;
  }
  
  .order-request-dialog .vs-popup {
    width: 450px !important;
    border-radius: 16px !important;
  }
  
  .order-request-dialog .vs-popup--header {
    display: none !important;
  }
  
  .product-qty-icon i {
    font-size: 20px;
  }
  [dir] .order-request-dialog .vs-popup--header .vs-popup--title {
    background-color: white  !important;
  }
  [dir] .order-request-dialog .vs-popup--header .vs-popup--title h3 {
    display: block !important;
  }
</style>
